;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { cva, type VariantProps } from 'class-variance-authority'
import { ReactNode } from 'react'

import { cn } from '../../utils/cn'
import { Tooltip } from './_tooltip/_tooltip'
import { type HelpContent } from './_tooltip/_tooltip-content'
import { getHelpAccessibleName } from './_tooltip/get-accessible-name'
import { BaseButton } from './base-button'
import { Icon, IconName } from './icon'

export const labeledBadgeVariants = cva(
  'inline-flex items-center gap-1 border py-0.5 text-center font-light',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-gray-100 text-gray-900',
        outlined: 'bg-default border-gray-900 text-gray-900',
        black: 'border-transparent bg-black text-white',
        red: 'bg-error-50 text-error-900 border-error-50',
        info: 'bg-info-50 text-info-900 border-info-50',
        marketing:
          'text-marketing-navy bg-marketing-lavender-bright border-marketing-lavender-bright',
        success: 'bg-success-50 text-success-900 border-success-50',
        caution: 'bg-caution-50 text-caution-900 border-caution-50',
        feature:
          'border-[0.5px] border-gray-700 bg-gradient-to-r from-[#FFFBEB] from-[0%] to-[#F1F1FF] to-[75.3%]',
      },
      size: {
        small: 'px-2.5 text-xs',
        medium: 'px-3 text-sm',
      },
      shape: {
        rounded: 'rounded-full',
        square: 'rounded-sm',
      },
      emphasis: {
        high: '',
        low: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'medium',
      shape: 'rounded',
    },
  },
)

export const iconOnlyBadgeVariants = cva(
  'inline-grid size-6.5 place-content-center rounded-full p-0 text-center font-light',
  {
    variants: {
      variant: {
        default: 'bg-gray-100 text-gray-900',
        outlined: 'bg-default border border-gray-900 text-gray-900',
        black: 'bg-black text-white',
        red: 'bg-error-50 text-error-900',
        info: 'bg-info-50 text-info-900',
        marketing: 'text-marketing-navy bg-marketing-lavender-bright',
        success: 'bg-success-50 text-success-900',
        caution: 'bg-caution-50 text-caution-900',
        feature:
          'bg-gradient-to-r from-[#FFFBEB] from-[0%] to-[#F1F1FF] to-[75.3%]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

export type BadgeEmphasis = NonNullable<
  VariantProps<typeof labeledBadgeVariants>['emphasis']
>

type BadgeVariant = NonNullable<
  VariantProps<typeof labeledBadgeVariants>['variant']
>
type BadgeSize = NonNullable<VariantProps<typeof labeledBadgeVariants>['size']>
type BadgeShape = NonNullable<
  VariantProps<typeof labeledBadgeVariants>['shape']
>

type BadgeSharedProps = {
  help?: HelpContent
  rumTooltipName?: string
  className?: string
  translate?: 'no'
}

type LabeledBadgeBaseProps = BadgeSharedProps & {
  variant?: BadgeVariant
}

type HighEmphasisBadgeProps = LabeledBadgeBaseProps & {
  icon?: IconName
  size?: BadgeSize
  shape?: BadgeShape
  emphasis: 'high'
  children: ReactNode
}

type LowEmphasisBadgeProps = LabeledBadgeBaseProps & {
  icon: IconName
  emphasis: 'low'
  children: ReactNode
  size?: never
  shape?: never
}

type IconOnlyBadgeProps = {
  variant?: BadgeVariant
  icon: IconName
  help: HelpContent
  rumTooltipName?: string
  className?: string
  translate?: 'no'
  children?: never
  emphasis?: never
  size?: never
  shape?: never
}

type BadgeProps =
  | HighEmphasisBadgeProps
  | LowEmphasisBadgeProps
  | IconOnlyBadgeProps

export function Badge(props: BadgeProps) {
  if (props.emphasis === 'high' || props.emphasis === 'low') {
    return <LabeledBadge {...props} />
  }

  return <IconOnlyBadge {...props} />
}

function IconOnlyBadge({
  className,
  variant,
  icon,
  help,
  rumTooltipName,
  ...props
}: IconOnlyBadgeProps) {
  const badgeClassName = cn(iconOnlyBadgeVariants({ variant }), className)

  return (
    <Tooltip help={help} rumTooltipName={rumTooltipName}>
      <BaseButton
        className={cn(badgeClassName, 'cursor-help')}
        aria-label={getHelpAccessibleName(help)}
        {...props}
      >
        <Icon className="size-4.5" name={icon} />
      </BaseButton>
    </Tooltip>
  )
}

function LabeledBadge({
  className,
  variant,
  size,
  shape,
  emphasis,
  icon,
  help,
  rumTooltipName,
  children,
  ...props
}: HighEmphasisBadgeProps | LowEmphasisBadgeProps) {
  if (emphasis === 'low') {
    return (
      <LowEmphasisBadge
        className={className}
        variant={variant}
        icon={icon}
        help={help}
        rumTooltipName={rumTooltipName}
        {...props}
      >
        {children}
      </LowEmphasisBadge>
    )
  }

  const badgeClassName = cn(
    labeledBadgeVariants({ variant, size, shape, emphasis }),
    className,
  )

  if (help) {
    return (
      <Tooltip help={help} rumTooltipName={rumTooltipName}>
        <BaseButton className={cn(badgeClassName, 'cursor-help')} {...props}>
          {icon ? <Icon className="size-4.5" name={icon} /> : null}
          {children}
        </BaseButton>
      </Tooltip>
    )
  }

  return (
    <span className={badgeClassName} {...props}>
      {icon ? <Icon className="size-4.5" name={icon} /> : null}
      {children}
    </span>
  )
}

function LowEmphasisBadge({
  className,
  variant,
  icon,
  help,
  rumTooltipName,
  children,
  ...props
}: Omit<LowEmphasisBadgeProps, 'emphasis'>) {
  const wrapperClassName = cn('inline-flex items-center gap-1', className)
  const iconClassName = iconOnlyBadgeVariants({ variant })

  const content = (
    <>
      <span className={iconClassName}>
        <Icon className="size-4.5" name={icon} />
      </span>
      <span className="typography-body-xs-normal text-default">{children}</span>
    </>
  )

  if (help) {
    return (
      <Tooltip help={help} rumTooltipName={rumTooltipName}>
        <BaseButton className={cn(wrapperClassName, 'cursor-help')} {...props}>
          {content}
        </BaseButton>
      </Tooltip>
    )
  }

  return (
    <span className={wrapperClassName} {...props}>
      {content}
    </span>
  )
}
